/**
 * CloseMemory — what this visitor has said no to.
 *
 * A visitor who closes a popup has answered it. Before this, the next visit
 * started from nothing: the session cap and the cooldown live in
 * sessionStorage and die with the tab, so the welcome popup someone closed
 * yesterday greeted them again today, and the day after. Guidance once
 * promised "24-48 hours after a dismissal" and nothing enforced it.
 *
 * localStorage, on purpose: unlike the per-visit counts, this must outlive
 * the tab. It is per browser and per site, which is what "this visitor" means
 * to a script on a page. Only an explicit close counts — the close button, an
 * outside tap, Escape. A popup that timed out, was replaced, or closed because
 * the visitor signed up was not refused.
 *
 * The numbers come from ONSITE_POLICY, the same place the agent reads them.
 */

import { ONSITE_POLICY, type CloseRestReason } from '@hamla/shared/onsite';

const STORAGE_KEY = 'hamla_closed_v1';
/** Campaigns remembered at most; the oldest close is forgotten first. */
const MAX_REMEMBERED = 50;

interface Closes {
  /** Closes counted toward `closesBeforeStop`. */
  count: number;
  /** When the last one happened, ms since epoch. */
  last: number;
}

type Store = Record<string, Closes>;

function read(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const store: Store = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      const v = value as Partial<Closes> | null;
      if (v && typeof v.count === 'number' && typeof v.last === 'number') store[id] = { count: v.count, last: v.last };
    }
    return store;
  } catch {
    // Private mode, blocked storage, corrupt JSON: fail open. Showing a popup
    // again is a smaller harm than a storage quirk silencing it for good.
    return {};
  }
}

function write(store: Store): void {
  try {
    const entries = Object.entries(store).sort(([, a], [, b]) => b.last - a.last).slice(0, MAX_REMEMBERED);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Storage unavailable: nothing to keep, and nothing to break.
  }
}

export class CloseMemory {
  constructor(private readonly now: () => number = Date.now) {}

  /** The visitor closed this campaign's popup. */
  recordClose(campaignId: string): void {
    const store = read();
    const at = this.now();
    const previous = store[campaignId];
    // A close after the long stop has run out starts a fresh count.
    const expired = previous && at - previous.last >= ONSITE_POLICY.stopAfterClosesMs;
    store[campaignId] = { count: !previous || expired ? 1 : previous.count + 1, last: at };
    write(store);
  }

  /** Why this campaign's popup must rest for this visitor now, or null when it may show. */
  resting(campaignId: string): CloseRestReason | null {
    const closes = read()[campaignId];
    if (!closes) return null;
    const since = this.now() - closes.last;
    if (closes.count >= ONSITE_POLICY.closesBeforeStop && since < ONSITE_POLICY.stopAfterClosesMs) {
      return 'closed_too_often';
    }
    if (since < ONSITE_POLICY.restAfterCloseMs) return 'closed_recently';
    return null;
  }
}
